import { create } from "zustand";
import type { Session, User as AuthUser } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { identify, resetAnalytics, track } from "@/lib/observability";
import { clearAllDrafts } from "@/lib/drafts";
import { LANDING_DEMO_URL } from "@/lib/constants";
import type { Tenant, User } from "@/types/database";

/** unlinked = session Supabase OK mais aucune ligne public.users pour cet id (typique après magic link + seed démo) */
type Status = "loading" | "anonymous" | "authenticated" | "unlinked";

let removeAuthListener: (() => void) | undefined;

/** Messages Auth plus clairs (rate limits, etc.) */
export function mapAuthErrorMessage(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = raw.toLowerCase();
  if (
    m.includes("rate limit") ||
    m.includes("too many requests") ||
    m.includes("429") ||
    m.includes("email rate limit")
  ) {
    return (
      "Limite d’envoi d’emails atteinte côté Supabase (trop de liens magiques demandés en peu de temps, pour ton adresse ou pour tout le projet). " +
      "Attends quelques minutes puis réessaie. Pour augmenter les plafonds : Dashboard Supabase → Authentication → Rate Limits. " +
      "Avec le SMTP intégré de Supabase, il existe aussi un quota horaire global : configure un SMTP personnalisé (Authentication → SMTP Settings) pour des volumes plus élevés."
    );
  }
  if (
    (m.includes("provider") && (m.includes("disabled") || m.includes("not enabled") || m.includes("invalid"))) ||
    m.includes("unsupported provider") ||
    (m.includes("google") && m.includes("not configured"))
  ) {
    return (
      "La connexion Google n’est pas activée ou mal configurée. Dans Supabase : Authentication → Providers → Google (activer + Client ID / Secret depuis Google Cloud Console). " +
      "URI de redirection autorisée côté Google : https://<project-ref>.supabase.co/auth/v1/callback"
    );
  }
  if (m.includes("unexpected_failure") || m.includes("unexpected failure") || m.includes("invalid character")) {
    return (
      "La configuration Auth Supabase est invalide (souvent un espace en trop dans Site URL). " +
      "Dashboard → Authentication → URL Configuration : Site URL doit être exactement https://app.dker.pro (sans espace)."
    );
  }
  if (m.includes("invalid login credentials") || m.includes("invalid email or password")) {
    return (
      "Email ou mot de passe incorrect. Si tu n’as pas encore de mot de passe (compte Google ou lien magique), " +
      "utilise « Mot de passe oublié » pour en définir un, ou reconnecte-toi avec Google."
    );
  }
  if (m.includes("email not confirmed")) {
    return "Cette adresse email n’est pas encore confirmée. Ouvre le lien reçu par email, ou demande un lien magique.";
  }
  if (
    m.includes("password is known to be weak") ||
    m.includes("pwned") ||
    m.includes("leaked") ||
    m.includes("compromised")
  ) {
    return "Ce mot de passe est trop courant ou a fuité. Choisis-en un autre, plus long et unique.";
  }
  if (m.includes("password") && (m.includes("at least") || m.includes("too short") || m.includes("6 characters"))) {
    return "Le mot de passe est trop court (12 caractères minimum).";
  }
  if (m.includes("same password") || m.includes("should be different")) {
    return "Le nouveau mot de passe doit être différent de l’ancien.";
  }
  return raw;
}

function authRedirectTo(path = "/login"): string {
  return `${window.location.origin.replace(/\/+$/, "")}${path}`;
}

export function mapRegisterTenantError(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = raw.toLowerCase();
  if (m.includes("already_registered")) {
    return "Ce compte a déjà un profil métier. Recharge la page ou déconnecte-toi puis reconnecte-toi.";
  }
  if (m.includes("not_authenticated")) {
    return "Session expirée. Déconnecte-toi puis reconnecte-toi.";
  }
  if (m.includes("invalid_company_name")) {
    return "Nom d’entreprise invalide (2 à 120 caractères).";
  }
  if (m.includes("invalid_trade_type")) {
    return "Corps de métier non reconnu. Choisis une valeur dans la liste.";
  }
  if (m.includes("signup_not_granted")) {
    return (
      "Cette adresse n’est pas encore autorisée à créer une entreprise : l’espace se crée lors de la mise en service. " +
      "Si vous l’avez déjà commandée, connectez-vous avec l’adresse donnée à DKER. Sinon, prenez rendez-vous."
    );
  }
  if (m.includes("anonymous_forbidden")) {
    return "Une session de démonstration ne peut pas créer d’entreprise. Déconnecte-toi, puis connecte-toi avec ton adresse.";
  }
  return raw;
}

/**
 * Les refus de `claim_user_invitation`.
 *
 * Un seul message côté base pour quatre échecs — inconnu, expiré, révoqué,
 * déjà consommé —, et on ne cherche pas à les distinguer ici : on ne le
 * pourrait pas, et c'est le but. Distinguer, ce serait dire à qui cherche
 * quels codes existent. La conduite à tenir est de toute façon la même :
 * redemander un code au patron.
 */
export function mapJoinCodeError(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = raw.toLowerCase();
  if (m.includes("invalid_code")) {
    return (
      "Ce code ne fonctionne pas. Il a peut-être déjà servi, ou dépassé ses " +
      "sept jours. Demandez-en un nouveau à votre patron : il en génère un en " +
      "deux gestes depuis l’écran Équipe."
    );
  }
  if (m.includes("already_has_profile")) {
    return "Ce compte appartient déjà à une entreprise. Déconnecte-toi puis reconnecte-toi avec le bon compte.";
  }
  if (m.includes("not_authenticated")) {
    return "Session expirée. Déconnecte-toi puis reconnecte-toi.";
  }
  return raw;
}

export function mapDemoSeedAttachError(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = raw.toLowerCase();
  if (m.includes("demo_seed_forbidden")) {
    return "Le rattachement au compte démo n’est pas autorisé pour cette adresse email.";
  }
  if (m.includes("already_has_profile")) {
    return "Un profil métier existe déjà pour ce compte.";
  }
  if (m.includes("not_authenticated")) {
    return "Session expirée. Déconnecte-toi puis reconnecte-toi.";
  }
  return raw;
}

/**
 * Les refus de la démonstration.
 *
 * `anonymous_provider_disabled` : les sessions anonymes ne sont pas ouvertes
 * dans Supabase (Authentication → Sign In / Providers → « Allow anonymous
 * sign-ins »). C'est un réglage du projet, pas une panne du visiteur.
 */
export function mapDemoError(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = raw.toLowerCase();
  if (m.includes("anonymous") && (m.includes("disabled") || m.includes("not enabled"))) {
    return "La démonstration n’est pas encore ouverte sur ce serveur. Réessayez un peu plus tard, ou prenez rendez-vous.";
  }
  if (m.includes("demo_capacity") || m.includes("rate limit") || m.includes("too many")) {
    return "Beaucoup de démonstrations sont en cours : réessayez dans quelques minutes.";
  }
  if (m.includes("demo_requires_anonymous_session")) {
    return "Vous êtes connecté avec un vrai compte. Déconnectez-vous pour ouvrir la démonstration.";
  }
  return "La démonstration n’a pas pu se préparer. Réessayez dans un instant.";
}

/**
 * Une préparation à la fois. Ouvrir la session anonyme fait repasser l'écran
 * par le chargement, ce qui remonte la page de démo et relancerait l'appel :
 * les deux appels partageraient alors la même session, et le second
 * échouerait sur le profil que le premier vient de créer.
 */
let demoEnCours: Promise<{ error: string | null }> | null = null;

interface AuthState {
  status: Status;
  authUser: AuthUser | null;
  user: User | null;
  tenant: Tenant | null;
  unreadAlerts: number;
  /** Session ouverte via le lien « mot de passe oublié » : il faut choisir un nouveau mot de passe. */
  passwordRecovery: boolean;
  init: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: string | null }>;
  /** Crée un nouveau tenant + profil owner (RPC sécurisée côté Postgres) et enregistre le mot de passe. */
  registerNewTenant: (
    companyName: string,
    tradeType: string,
    password: string,
  ) => Promise<{ error: string | null }>;
  /**
   * Rejoint l’entreprise qui a émis le code (RPC à usage unique).
   *
   * Le troisième appel Supabase direct de ce fichier, et il a la même raison
   * d’être que les deux autres : l’appelant n’a pas encore de profil, donc pas
   * de tenant ni de rôle — il n’y a aucun `ExecutionContext` à construire, donc
   * aucun service à traverser. C’est de l’amorçage de session, pas du métier.
   */
  joinWithCode: (code: string) => Promise<{ error: string | null }>;
  /** Rattache au tenant seed démo (RPC réservée à l’email autorisé). */
  attachDemoSeedTenant: () => Promise<{ error: string | null }>;
  /**
   * Ouvre une démonstration : session anonyme, puis entreprise fictive remplie
   * par `start_demo_sandbox()`. Reprend le bac à sable de la session s'il
   * existe déjà. Quatrième amorçage de session de ce fichier, pour la même
   * raison que les trois autres : il n'y a pas encore de profil.
   */
  startDemo: () => Promise<{ error: string | null }>;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  setPassword: (password: string) => Promise<{ error: string | null }>;
  requestPasswordReset: (email: string) => Promise<{ error: string | null }>;
  /**
   * `restartDemo` : « Repartir de zéro » depuis le bandeau de démonstration,
   * qui enchaîne sur un bac à sable neuf. Sans lui, un visiteur de la démo qui
   * se déconnecte est renvoyé sur la landing, pas sur l'écran de connexion :
   * il n'a pas de compte, et rien à y faire.
   */
  signOut: (options?: { restartDemo?: boolean }) => Promise<void>;
  refreshTenant: () => Promise<void>;
  setUnreadAlerts: (n: number) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "loading",
  authUser: null,
  user: null,
  tenant: null,
  unreadAlerts: 0,
  passwordRecovery: false,

  async init() {
    removeAuthListener?.();
    const { data } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === "PASSWORD_RECOVERY") set({ passwordRecovery: true });
      void applySession(newSession, set);
    });
    removeAuthListener = () => data.subscription.unsubscribe();

    const { data: sessionData } = await supabase.auth.getSession();
    await applySession(sessionData.session, set);
  },

  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: authRedirectTo(),
        queryParams: { access_type: "offline", prompt: "consent" },
      },
    });
    if (error) return { error: mapAuthErrorMessage(error.message) };
    if (data.url) {
      window.location.assign(data.url);
      return { error: null };
    }
    return {
      error:
        "Impossible de démarrer la connexion Google. Vérifie que le fournisseur Google est activé dans Supabase (Authentication → Providers → Google).",
    };
  },

  async signInWithMagicLink(email) {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Même origine que l’app : après clic email, PKCE échange le code sur cette URL
        emailRedirectTo: authRedirectTo(),
      },
    });
    return { error: mapAuthErrorMessage(error?.message) };
  },

  async registerNewTenant(companyName, tradeType, password) {
    const { error: passwordError } = await supabase.auth.updateUser({ password });
    if (
      passwordError &&
      !/same password|should be different/i.test(passwordError.message)
    ) {
      return { error: mapAuthErrorMessage(passwordError.message) };
    }
    const { error } = await supabase.rpc("register_new_tenant", {
      p_company_name: companyName,
      p_trade_type: tradeType,
    });
    if (error) return { error: mapRegisterTenantError(error.message) };
    const { data: sessionData } = await supabase.auth.getSession();
    await applySession(sessionData.session, set);
    return { error: null };
  },

  async joinWithCode(code) {
    const { error } = await supabase.rpc("claim_user_invitation", { p_code: code });
    if (error) return { error: mapJoinCodeError(error.message) };
    // Relire la session : le profil vient de naître, et c'est lui qui décide
    // de tout le reste — le rôle, donc le menu, donc l'écran d'atterrissage.
    const { data: sessionData } = await supabase.auth.getSession();
    await applySession(sessionData.session, set);
    return { error: null };
  },

  async attachDemoSeedTenant() {
    const { error } = await supabase.rpc("attach_demo_seed_tenant");
    if (error) return { error: mapDemoSeedAttachError(error.message) };
    const { data: sessionData } = await supabase.auth.getSession();
    await applySession(sessionData.session, set);
    return { error: null };
  },

  startDemo() {
    demoEnCours ??= (async () => {
      try {
        const { data: courante } = await supabase.auth.getSession();
        if (courante.session && !courante.session.user.is_anonymous) {
          return { error: mapDemoError("demo_requires_anonymous_session") };
        }
        if (!courante.session) {
          const { error } = await supabase.auth.signInAnonymously();
          if (error) return { error: mapDemoError(error.message) };
        }
        const { error } = await supabase.rpc("start_demo_sandbox");
        if (error) return { error: mapDemoError(error.message) };
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) await loadProfile(sessionData.session.user, set);
        track("demo_started");
        return { error: null };
      } finally {
        demoEnCours = null;
      }
    })();
    return demoEnCours;
  },

  async signInWithPassword(email, password) {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    return { error: mapAuthErrorMessage(error?.message) };
  },

  async setPassword(password) {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) return { error: mapAuthErrorMessage(error.message) };
    set({ passwordRecovery: false });
    const { data: sessionData } = await supabase.auth.getSession();
    await applySession(sessionData.session, set);
    return { error: null };
  },

  async requestPasswordReset(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: authRedirectTo(),
    });
    return { error: mapAuthErrorMessage(error?.message) };
  },

  async signOut(options) {
    const etaitDemo = get().authUser?.is_anonymous === true;
    await supabase.auth.signOut();
    resetAnalytics();
    // Une fiche client à moitié tapée est une donnée personnelle, et la
    // tablette du chantier passe de main en main : se déconnecter, c'est ne
    // rien laisser derrière soi. Voir `lib/drafts.ts`.
    try {
      clearAllDrafts(window.localStorage);
    } catch {
      /* stockage inaccessible : il n'y a rien à effacer */
    }
    set({
      status: "anonymous",
      authUser: null,
      user: null,
      tenant: null,
      passwordRecovery: false,
    });
    if (etaitDemo && !options?.restartDemo) window.location.assign(LANDING_DEMO_URL);
  },

  async refreshTenant() {
    const tenantId = get().user?.tenant_id;
    if (!tenantId) return;
    const tenant = await fetchTenant(tenantId);
    if (tenant) set({ tenant });
  },

  setUnreadAlerts(n) {
    set({ unreadAlerts: n });
  },
}));

/**
 * La fiche entreprise de la session.
 *
 * Le bureau lit la ligne `tenants`. Un compte ouvrier ne la voit pas — elle
 * porte l'IBAN et les identifiants de la plateforme agréée, voir la migration
 * `20260301005400_tenant_row_office_only` — et reçoit à la place la projection
 * de `my_tenant_profile()` : nom, métier, plan, logo, couleur. Les autres
 * champs restent absents ; aucun écran de l'espace ouvrier ne les lit.
 *
 * La ligne d'abord, la projection ensuite, plutôt qu'un aiguillage sur le
 * rôle : ce front fonctionne ainsi avant comme après la migration, quel que
 * soit l'ordre dans lequel le push les met en ligne.
 */
async function fetchTenant(tenantId: string): Promise<Tenant | null> {
  const { data: ligne } = await supabase
    .from("tenants")
    .select("*")
    .eq("id", tenantId)
    .maybeSingle();
  if (ligne) return ligne as Tenant;

  const { data: profil } = await supabase.rpc("my_tenant_profile").maybeSingle();
  return profil ? (profil as Tenant) : null;
}

async function applySession(session: Session | null, set: (s: Partial<AuthState>) => void) {
  if (!session) {
    set({ status: "anonymous", authUser: null, user: null, tenant: null, passwordRecovery: false });
    return;
  }
  // La même personne, déjà chargée : on garde l'écran tel qu'il est.
  //
  // Supabase ré-annonce la session bien plus souvent qu'on ne se connecte :
  // `SIGNED_IN` chaque fois que l'onglet redevient visible (auth-js,
  // `_onVisibilityChanged` → `_recoverAndRefresh`), `TOKEN_REFRESHED` toutes
  // les heures. Chaque annonce relisait le profil en repassant par
  // `status: "loading"` — et `App` remplace alors toute l'application par
  // l'écran de chargement. Les pages étaient démontées, la saisie en cours
  // perdue. Le patron : « si la page se recharge, on perd tout ce qu'on avait
  // saisi » — il revenait d'un appel, d'un SMS, de l'appareil photo. Ce
  // n'était pas un rechargement, c'était ceci. Constaté le 13 septembre.
  //
  // Pas de relecture silencieuse non plus : un échec réseau passager au retour
  // sur l'onglet renverrait « unlinked », donc l'écran de connexion — la même
  // perte par un autre chemin. Ce qui change vraiment le profil
  // (inscription, code d'invitation) part d'un statut non authentifié et
  // relit donc bien ; le logo de l'entreprise a `refreshTenant`.
  const courant = useAuthStore.getState();
  if (courant.status === "authenticated" && courant.user?.id === session.user.id) {
    set({ authUser: session.user });
    return;
  }
  await loadProfile(session.user, set);
}

async function loadProfile(authUser: AuthUser, set: (s: Partial<AuthState>) => void) {
  set({ authUser, status: "loading" });
  const { data: userRow } = await supabase
    .from("users")
    .select("*")
    .eq("id", authUser.id)
    .single();

  if (!userRow) {
    set({
      status: "unlinked",
      authUser,
      user: null,
      tenant: null,
    });
    return;
  }

  const tenantRow = await fetchTenant((userRow as User).tenant_id);

  if (!tenantRow) {
    set({
      status: "unlinked",
      authUser,
      user: null,
      tenant: null,
    });
    return;
  }

  const user = userRow as User;
  const tenant = tenantRow;
  identify(user.id, { tenant_id: user.tenant_id, role: user.role, plan: tenant.plan, trade: tenant.trade_type });
  track("session_started");

  set({
    status: "authenticated",
    user,
    tenant,
  });
}
